import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { FaTimes } from "react-icons/fa";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import NavbarContainer from "../../../components/NavbarContainer";
import { MEMBERSHIP_UPGRADED } from "../../../utils/strings";
import ReferralSection from "../../../components/ReferralSection";
import {
  getUserMembershipStatusCall,
  subscribeToNextGymCall,
  undoNextGymCall,
  previewGymSwitchCall,
  switchCurrentGymCall,
} from "../../../api/apiOperations";
import { undoCancelCurrentMembership } from "../../../api/apiEndpoints";
import { useApi } from "../../../hooks";
import { formatDate, sortGymsByCustomOrder, getGymLogoUrl } from "../../../utils/utils";
import { setIsNextGymSelected } from "../../../slices/appSlice";
import { Business } from "../../../utils/types";
import {
  Container,
  ModalOverlay,
  ModalContainer,
  ModalHeader,
  ModalTitle,
  CloseButton,
  GymGridContainer,
  GymGridItem,
  GymGridItemContent,
  GymGridImageContainer,
  GymGridImage,
  GymGridDetails,
  GymGridName,
  GymGridAddress,
  UnavailableBadge,
  ActivationContainer,
  ActivationTitle,
  ActivationDescription,
  ActivationButton,
  RequirementBadge,
  FilterChip,
  MsOverlay,
  MsLabel,
  MsTitle,
  MsSubtitle,
  MsPrice,
  MsPrimaryBtn,
  MsSecondaryBtn,
  MsTextLink,
  HeroSection,
  HeroGreeting,
  HeroGymName,
  ContentCard,
  GymMiniRow,
  GymMiniLogo,
  MembershipRow,
  MembershipRowText,
  ManageButton,
  CheckInButton,
} from "./styles";
import { useNavigate } from "react-router-dom";
import FailedPaymentSheet from "./FailedPaymentSheet";

// ── Constants ──────────────────────────────────────────────────────────────
const QUOTES = [
  'Good days are made, not had.',
  'Show up. The rest follows.',
  'One rep closer.',
  'Progress over perfection.',
  'Earn it daily.',
  'Strong starts here.',
  'Make today count.',
];

const getDailyQuote = () => QUOTES[new Date().getDay() % QUOTES.length];

const getGreeting = () => {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
};

const TERRITORIES = [
  { id: 'miami',           label: 'Miami',          latitude: 25.7617, longitude: -80.1918, radiusMi: 18 },
  { id: 'coral_gables',    label: 'Coral Gables',   latitude: 25.7215, longitude: -80.2684, radiusMi: 10 },
  { id: 'fort_lauderdale', label: 'Fort Lauderdale', latitude: 26.1224, longitude: -80.1373, radiusMi: 15 },
  { id: 'new_york',        label: 'New York',       latitude: 40.7128, longitude: -74.0060, radiusMi: 35 },
  { id: 'tampa',           label: 'Tampa',          latitude: 27.9506, longitude: -82.4572, radiusMi: 35 },
];

const ALL_AMENITIES = [
  '24/7 Access', 'Big 3', 'Cold Plunge', 'Group classes', 'Lockers',
  'Nutrition Bar', 'Personal training', 'Red Light Therapy', 'Sauna',
  'Showers', 'Spinning', 'Steam Room', 'Swimming pool', 'Tanning',
];

const SOLD_OUT_GYMS = [
  "AIRLAB Fitness", "CKO EDGEWATER", "Bloom Yoga",
  "Legacy Coral Gables (Classes Only)", "Legacy Fort Lauderdale (Classes Only)",
  "Legacy Wynwood (Classes Only)",
];

const haversineDistanceDash = (lat1: number, lon1: number, lat2: number, lon2: number) => {
  const R = 3959;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const formatProductName = (name: string) => {
  switch (name) {
    case 'basic': return 'Basic Trial';
    case 'premium': return 'Premium Monthly';
    case 'premium_quarterly': return 'Premium Quarterly';
    case 'premium_yearly': return 'Premium Annual';
    default: return name;
  }
};

// ── Component ──────────────────────────────────────────────────────────────
interface RootState {
  appData: {
    allBusiness: Business[];
    userMembershipStatus: any;
    businessProfile: any;
    membershipUpgraded: boolean;
    isNextGymSelected: boolean;
    userCanDowngradeToBasic: boolean;
    userData: { firstName?: string; lastName?: string };
  };
  auth: { memberLoggedIn: boolean; idToken: string };
}

const DashboardMembers: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);

  // Modals
  const [showCheckInModal, setShowCheckInModal] = useState(false);
  const [showManageModal, setShowManageModal] = useState(false);
  const [showGymPicker, setShowGymPicker] = useState(false);
  const [pickerMode, setPickerMode] = useState<'next' | 'current'>('next');
  const [showQueueConfirm, setShowQueueConfirm] = useState(false);
  const [pendingNextGym, setPendingNextGym] = useState<Business | null>(null);
  const [queueLoading, setQueueLoading] = useState(false);
  const [showStayModal, setShowStayModal] = useState(false);
  const [stayConfirmLoading, setStayConfirmLoading] = useState(false);
  const [showSwitchConfirm, setShowSwitchConfirm] = useState(false);
  const [switchPreviewData, setSwitchPreviewData] = useState<any>(null);
  const [pendingSwitchGym, setPendingSwitchGym] = useState<Business | null>(null);
  const [switchLoading, setSwitchLoading] = useState(false);
  const [showRemainMemberModal, setShowRemainMemberModal] = useState(false);
  const [remainMemberError, setRemainMemberError] = useState(false);
  const [remainMemberSuccess, setRemainMemberSuccess] = useState(false);
  const [undoCancelLoading, setUndoCancelLoading] = useState(false);

  // Gym picker filters
  const [gymSearchQuery, setGymSearchQuery] = useState('');
  const [filterTerritory, setFilterTerritory] = useState<string | null>(null);
  const [filterAmenities, setFilterAmenities] = useState<string[]>([]);

  const { allBusiness, userMembershipStatus, businessProfile, membershipUpgraded, userData } =
    useSelector((state: RootState) => state.appData);

  const { request: getUserMembershipStatusCallReq } = useApi<any>({ apiFunc: getUserMembershipStatusCall });

  // Direct fetch bypassing useApi wrapper — ensures dispatch always fires
  const refreshMembership = async () => {
    try {
      await getUserMembershipStatusCall();
    } catch (err) {
      console.error('[Dashboard] Failed to refresh membership status:', err);
    }
  };

  const { memberships, settings } = userMembershipStatus || {};
  const pastDue = !!userMembershipStatus?.pastDue;
  const { name, businessId: currentGymId } = memberships?.active?.business || {};
  const { expiresAt } = memberships?.active || {};
  const activeProductName: string = memberships?.active?.product?.name ?? '';
  const isPremiumMember = ['premium', 'premium_quarterly', 'premium_yearly'].includes(activeProductName);
  // Derive directly from API response — never rely on the persisted isNextGymSelected flag
  const hasNextGym = !!(settings?.nextBusinessId);
  const isMoving = hasNextGym && settings?.nextBusinessId !== currentGymId;
  const isRenewing = hasNextGym && settings?.nextBusinessId === currentGymId;
  // Find the gym's active monthly premium product to show the correct renewal price.
  // Don't use the active membership's preTaxCost — that's the trial/basic price.
  const currentGymPremiumProduct = businessProfile?.products?.find(
    (p: any) => p.name === 'premium' && p.is_active !== false
  );
  const currentGymPremiumPrice: number | null = currentGymPremiumProduct
    ? Number(currentGymPremiumProduct.price?.preTaxCost ?? currentGymPremiumProduct.preTaxCost ?? 0)
    : null;
  const firstName = userData?.firstName || '';

  // Gym address from businessProfile
  const gymAddress = businessProfile?.address
    ? (typeof businessProfile.address === 'string' ? businessProfile.address : businessProfile.address?.fullAddress ?? '')
    : '';
  const formattedAddress = gymAddress.includes(', ')
    ? gymAddress.split(', ').slice(0, 2).join(', ')
    : gymAddress;

  // Sort and filter gyms for picker
  const customOrderedGyms = sortGymsByCustomOrder(allBusiness, "SWEAT440 Midtown Miami");

  const isGymSoldOut = (gym: Business) => SOLD_OUT_GYMS.includes(gym.name);

  const getGymCooldownInfo = (gym: Business): { inCooldown: boolean; remainingTime?: string } => {
    if (!userMembershipStatus?.memberships) return { inCooldown: false };
    if (gym.name === name) return { inCooldown: true, remainingTime: 'Your current gym' };
    if (isGymSoldOut(gym)) return { inCooldown: true, remainingTime: 'Sold Out' };
    const history = userMembershipStatus.memberships?.history || [];
    const sorted = [...history].sort((a: any, b: any) => new Date(a.startedAt).getTime() - new Date(b.startedAt).getTime());
    const gymHistory = sorted.filter((m: any) => m.business?.name === gym.name);
    if (gymHistory.length === 0) return { inCooldown: false };
    const recent = gymHistory[gymHistory.length - 1];
    const pos = sorted.findIndex((m: any) => m.business?.name === recent.business?.name && m.startedAt === recent.startedAt);
    const since = sorted.length - 1 - pos;
    if (since < 3) {
      const rem = 3 - since;
      return { inCooldown: true, remainingTime: `${rem} more membership${rem > 1 ? 's' : ''} required` };
    }
    return { inCooldown: false };
  };

  const availableGyms = customOrderedGyms.filter((gym: Business) => {
    if (isGymSoldOut(gym)) return true;
    return !getGymCooldownInfo(gym).inCooldown;
  });

  const filteredPickerGyms = availableGyms.filter((gym: Business) => {
    if (gymSearchQuery && !gym.name.toLowerCase().includes(gymSearchQuery.toLowerCase())) return false;
    const territory = TERRITORIES.find(t => t.id === filterTerritory);
    if (territory) {
      const lat = parseFloat((gym.address as any)?.latitude || '0');
      const lon = parseFloat((gym.address as any)?.longitude || '0');
      if (!lat && !lon) return false;
      if (haversineDistanceDash(territory.latitude, territory.longitude, lat, lon) > territory.radiusMi) return false;
    }
    if (filterAmenities.length > 0 && !filterAmenities.every(a => (gym.amenities ?? []).includes(a))) return false;
    return true;
  });

  useEffect(() => {
    setIsLoading(true);
    refreshMembership().finally(() => setIsLoading(false));
  }, []);

  // ── Handlers ──
  const openGymPicker = (mode: 'next' | 'current') => {
    setPickerMode(mode);
    setGymSearchQuery('');
    setFilterTerritory(null);
    setFilterAmenities([]);
    setShowGymPicker(true);
    setShowManageModal(false);
  };

  const handleGymSelected = async (gym: Business) => {
    setShowGymPicker(false);
    if (pickerMode === 'current') {
      // Preview price difference for switching current gym
      const basic = gym.products?.find((p: any) => p.name?.toLowerCase() === 'basic');
      if (!basic) {
        toast.error('No basic membership found for this gym.');
        return;
      }
      const preview = await previewGymSwitchCall(basic.businessProductId);
      if (!preview.success) {
        toast.error('Unable to switch gyms at this time. Please try again.');
        return;
      }
      // Always show the confirm modal so the user sees the new gym's renewal price
      // before committing — even if there's no immediate charge or credit.
      const difference = Math.abs(preview.data?.difference ?? 0);
      setPendingSwitchGym(gym);
      setSwitchPreviewData({ ...preview.data, businessProductId: basic.businessProductId, difference, newTotal: preview.data?.newProduct?.total ?? 0 });
      setShowSwitchConfirm(true);
    } else {
      // Queue next gym — show confirmation with pricing
      setPendingNextGym(gym);
      setShowQueueConfirm(true);
    }
  };

  const handleConfirmQueue = async () => {
    if (!pendingNextGym) return;
    setQueueLoading(true);
    try {
      const basic = pendingNextGym.products?.find((p: any) => p.name?.toLowerCase() === 'basic');
      if (basic) {
        await subscribeToNextGymCall(basic.businessProductId);
        await refreshMembership();
        dispatch(setIsNextGymSelected(true));
        toast.success(`${pendingNextGym.name} queued as your next gym!`, { position: 'top-center', autoClose: 3000 });
      } else {
        toast.error('No basic membership found for this gym.');
      }
    } catch {
      toast.error('Error selecting gym. Please try again.');
    } finally {
      setQueueLoading(false);
      setShowQueueConfirm(false);
      setPendingNextGym(null);
    }
  };

  const handleConfirmSwitch = async () => {
    if (!switchPreviewData) return;
    setSwitchLoading(true);
    const ok = await switchCurrentGymCall(switchPreviewData.businessProductId);
    setSwitchLoading(false);
    if (ok) {
      toast.success('Your current gym has been updated!');
      setShowSwitchConfirm(false);
      setSwitchPreviewData(null);
      await getUserMembershipStatusCallReq();
    } else {
      toast.error('Failed to update your gym. Please try again.');
    }
  };

  const handleStayAtGym = async () => {
    setStayConfirmLoading(true);
    try {
      await undoNextGymCall();
      await getUserMembershipStatusCallReq();
      dispatch(setIsNextGymSelected(false));
      setShowStayModal(false);
      toast.success(`You'll continue your membership at ${name}!`, { position: 'top-center', autoClose: 3000 });
    } catch {
      toast.error('Something went wrong. Please try again.', { position: 'top-center', autoClose: 3000 });
    } finally {
      setStayConfirmLoading(false);
    }
  };

  const handleOpenRemainMemberModal = () => {
    setShowManageModal(false);
    setRemainMemberError(false);
    setRemainMemberSuccess(false);
    setShowRemainMemberModal(true);
  };

  const handleUndoCancellation = async () => {
    setUndoCancelLoading(true);
    setRemainMemberError(false);
    try {
      const result = await undoCancelCurrentMembership();
      if (result?.status >= 200 && result?.status < 300) {
        await refreshMembership();
        setRemainMemberSuccess(true);
      } else {
        setRemainMemberError(true);
      }
    } catch {
      setRemainMemberError(true);
    } finally {
      setUndoCancelLoading(false);
    }
  };

  const handleDirections = () => {
    const addr = encodeURIComponent(gymAddress || name || '');
    window.open(`https://www.google.com/maps/search/?api=1&query=${addr}`, '_blank');
  };

  const membershipRowText = (() => {
    if (settings?.scheduledCancelAt) return `Membership ends on ${formatDate(settings.scheduledCancelAt)}`;
    if (isMoving) return `Moving to ${settings?.nextBusinessName} · ${formatDate(expiresAt)}`;
    return `Renews ${formatDate(expiresAt)}`;
  })();

  // ── Centered overlay helper ──
  const CenteredOverlay: React.FC<{ onClose: () => void; children: React.ReactNode }> = ({ onClose, children }) => (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '16px',
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        backgroundColor: '#faf6f1', borderRadius: '24px', padding: '32px 28px',
        maxWidth: '420px', width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
      }}>
        {children}
      </div>
    </div>
  );

  const Divider = () => <div style={{ height: '1px', backgroundColor: '#e8e2dc', margin: '20px 0' }} />;

  // ── Loading ──
  if (isLoading) {
    return (
      <Container>
        <NavbarContainer dashboardScreen />
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, minHeight: '60vh', gap: '16px' }}>
          <div style={{ border: '3px solid #e8e4de', borderTop: '3px solid #1a1a1a', borderRadius: '50%', width: '40px', height: '40px', animation: 'spin 0.8s linear infinite' }} />
          <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
          <span style={{ fontSize: '14px', color: '#888' }}>Loading your dashboard…</span>
        </div>
      </Container>
    );
  }

  // ── No membership ──
  if (!settings?.currentProductId) {
    return (
      <Container style={{ justifyContent: 'center', minHeight: '100vh' }}>
        <NavbarContainer dashboardScreen />
        <div style={{
          flex: 1, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          padding: '24px 16px',
        }}>
          <p style={{ fontSize: '15px', color: '#5a4e45', marginBottom: '8px', textAlign: 'center' }}>
            {getGreeting()}{firstName ? `, ${firstName}` : ''}
          </p>
          <ActivationContainer style={{ backgroundColor: '#fff', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', textAlign: 'center' }}>
            <ActivationTitle>Activate Your Membership</ActivationTitle>
            <ActivationDescription>
              Find a space where showing up is easy. Explore premier fitness facilities in your area.
            </ActivationDescription>
            <ActivationButton onClick={() => navigate('/select-gym')}>Browse locations</ActivationButton>
          </ActivationContainer>
        </div>
        <ToastContainer />
      </Container>
    );
  }

  // ── Active membership ──
  return (
    <Container>
      <NavbarContainer dashboardScreen />

      {/* ── Hero ── */}
      <HeroSection style={{ paddingBottom: '36px' }}>
        <HeroGreeting style={{ marginBottom: '4px', fontSize: '17px' }}>{getGreeting()}{firstName ? `, ${firstName}` : ''}</HeroGreeting>
      </HeroSection>

      {/* ── Content (centered, max-width 560px) ── */}
      <div style={{ width: '100%', maxWidth: '560px', padding: '0 16px 80px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '0' }}>

        {/* Cancellation banner */}
        {!!settings?.scheduledCancelAt && (
          <div style={{
            border: '1px solid #FECACA',
            backgroundColor: '#FFF5F5',
            borderRadius: '12px',
            padding: '14px 16px',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '14px',
            flexWrap: 'wrap',
          }}>
            <div style={{ minWidth: 0, flex: '1 1 260px' }}>
              <div style={{ color: '#B91C1C', fontWeight: 600, fontSize: '14px', marginBottom: '2px' }}>
                Membership ends on {formatDate(settings.scheduledCancelAt)}
              </div>
              <p style={{ color: '#4B5563', fontSize: '13px', margin: 0, lineHeight: 1.5 }}>Your access remains active until that date.</p>
            </div>
            <button
              onClick={handleOpenRemainMemberModal}
              style={{
                border: '1.5px solid #1a1a1a',
                backgroundColor: '#1a1a1a',
                color: '#ffffff',
                borderRadius: '999px',
                padding: '9px 18px',
                fontSize: '13px',
                fontWeight: 800,
                cursor: 'pointer',
                boxShadow: '0 6px 14px rgba(0, 0, 0, 0.10)',
                whiteSpace: 'nowrap',
              }}
            >
              Remain a member
            </button>
          </div>
        )}

        {/* White content card */}
        <ContentCard>
          {/* Check in + Directions */}
          <div style={{ display: 'flex', gap: '12px', marginBottom: '24px' }}>
            <CheckInButton style={{ flex: 1 }} onClick={() => setShowCheckInModal(true)}>Check in</CheckInButton>
            <button onClick={handleDirections} style={{
              flex: 1, backgroundColor: 'transparent', border: '1.5px solid #1a1a1a', borderRadius: '30px',
              padding: '12px', fontSize: '15px', fontWeight: 600, cursor: 'pointer', color: '#1a1a1a',
            }}>Directions</button>
          </div>

          {/* Quote */}
          <div style={{ height: '1px', backgroundColor: '#f0f0f0', marginBottom: '0' }} />
          <p style={{ fontStyle: 'italic', color: '#9CA3AF', fontSize: '15px', textAlign: 'center', margin: '16px 0' }}>{getDailyQuote()}</p>
          <div style={{ height: '1px', backgroundColor: '#f0f0f0', marginBottom: '20px' }} />

          {/* Gym mini card */}
          <GymMiniRow>
            <GymMiniLogo
              src={getGymLogoUrl(name) || `https://via.placeholder.com/52x52?text=${name?.charAt(0)}`}
              alt={name}
              onError={(e) => { e.currentTarget.src = `https://via.placeholder.com/52x52?text=${name?.charAt(0)}`; }}
            />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: '16px', color: '#1a1a1a', marginBottom: '2px' }}>{name}</div>
              <div style={{ fontSize: '13px', color: '#9CA3AF' }}>{formattedAddress || formatProductName(activeProductName)}</div>
            </div>
          </GymMiniRow>

          {/* Membership row */}
          <div style={{ height: '1px', backgroundColor: '#f0f0f0', margin: '16px 0' }} />
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
            <span style={{ fontSize: '13px', color: '#555', flex: 1, lineHeight: '1.4' }}>{membershipRowText}</span>
            <ManageButton onClick={() => setShowManageModal(true)} style={{ flexShrink: 0 }}>Manage</ManageButton>
          </div>
        </ContentCard>

        {/* Referral */}
        <div style={{ marginTop: '24px' }}>
          <ReferralSection />
        </div>
      </div>

      {/* ── Check-in Code Modal ── */}
      {showCheckInModal && (
        <CenteredOverlay onClose={() => setShowCheckInModal(false)}>
          <p style={{ fontSize: '11px', fontWeight: 700, color: '#9CA3AF', letterSpacing: '1.5px', textTransform: 'uppercase', margin: '0 0 8px', textAlign: 'center' }}>CHECK-IN CODE</p>
          <p style={{ fontSize: '14px', color: '#555', margin: '0 0 16px', textAlign: 'center' }}>Show this code to gym staff to complete your check-in.</p>
          <div style={{ fontSize: '36px', fontWeight: 700, fontFamily: 'monospace', letterSpacing: '4px', color: '#1a1a1a', margin: '0 0 24px', textAlign: 'center' }}>
            {memberships?.active?.checkInCode}
          </div>
          <MsPrimaryBtn onClick={() => setShowCheckInModal(false)}>Close</MsPrimaryBtn>
        </CenteredOverlay>
      )}

      {/* ── Manage Modal ── */}
      {showManageModal && (
        <CenteredOverlay onClose={() => setShowManageModal(false)}>
          {settings?.scheduledCancelAt ? (
            <>
              <MsLabel>CURRENT MEMBERSHIP</MsLabel>
              <MsTitle>Cancels {formatDate(settings.scheduledCancelAt)}</MsTitle>
              <MsSubtitle>at {name}</MsSubtitle>
              <Divider />
              <MsPrimaryBtn onClick={handleOpenRemainMemberModal}>
                Remain a member
              </MsPrimaryBtn>
            </>
          ) : isMoving ? (
            <>
              <MsLabel>CURRENT MEMBERSHIP</MsLabel>
              <MsTitle>Moving to {settings?.nextBusinessName}</MsTitle>
              <MsSubtitle>· {formatDate(expiresAt)}</MsSubtitle>
              <Divider />
              <MsPrimaryBtn onClick={() => { setShowManageModal(false); setShowStayModal(true); }}>
                Stay at {name}
              </MsPrimaryBtn>
              {!memberships?.active?.isOnboarded && (
                <MsSecondaryBtn onClick={() => openGymPicker('current')}>Change my current gym</MsSecondaryBtn>
              )}
              <MsTextLink onClick={() => openGymPicker('next')}>Trial a new gym next month</MsTextLink>
            </>
          ) : (
            <>
              <MsLabel>CURRENT MEMBERSHIP</MsLabel>
              <MsTitle>{isPremiumMember ? 'Continuing at' : 'Active at'} {name}</MsTitle>
              {currentGymPremiumPrice != null && <MsPrice>${Math.round(currentGymPremiumPrice)}/mo</MsPrice>}
              <Divider />
              {!memberships?.active?.isOnboarded && (
                <MsPrimaryBtn onClick={() => openGymPicker('current')}>Change my current gym</MsPrimaryBtn>
              )}
              <MsTextLink onClick={() => openGymPicker('next')}>Trial a new gym next month</MsTextLink>
            </>
          )}
        </CenteredOverlay>
      )}

      {/* ── Remain Member Confirmation ── */}
      {showRemainMemberModal && (
        <CenteredOverlay onClose={() => !undoCancelLoading && setShowRemainMemberModal(false)}>
          <div style={{ width: '40px', height: '4px', borderRadius: '2px', backgroundColor: '#d4cbc2', margin: '0 auto 24px' }} />
          {remainMemberSuccess ? (
            <>
              <MsTitle>You're staying active</MsTitle>
              <div style={{ fontSize: '15px', color: '#666', lineHeight: 1.6, margin: '12px 0' }}>
                Your membership will continue as normal and will not end at the close of this billing cycle.
              </div>
              <Divider />
              <MsPrimaryBtn onClick={() => setShowRemainMemberModal(false)}>Done</MsPrimaryBtn>
            </>
          ) : remainMemberError ? (
            <>
              <MsTitle>Something went wrong</MsTitle>
              <div style={{ fontSize: '15px', color: '#666', lineHeight: 1.6, margin: '12px 0' }}>
                We couldn't keep your membership active. Please try again or contact support.
              </div>
              <Divider />
              <MsPrimaryBtn onClick={() => setRemainMemberError(false)}>Try again</MsPrimaryBtn>
              <MsSecondaryBtn onClick={() => setShowRemainMemberModal(false)}>Go Back</MsSecondaryBtn>
            </>
          ) : (
            <>
              <MsTitle>Keep your membership active?</MsTitle>
              <div style={{ fontSize: '15px', color: '#666', lineHeight: 1.6, margin: '12px 0' }}>
                {`This will undo your scheduled cancellation${settings?.scheduledCancelAt ? ` on ${formatDate(settings.scheduledCancelAt)}` : ""}. Your membership will continue as normal.`}
              </div>
              <Divider />
              <MsPrimaryBtn onClick={handleUndoCancellation} disabled={undoCancelLoading}>
                {undoCancelLoading ? 'Processing...' : 'Remain a member'}
              </MsPrimaryBtn>
              <MsSecondaryBtn onClick={() => setShowRemainMemberModal(false)} disabled={undoCancelLoading}>
                Go Back
              </MsSecondaryBtn>
            </>
          )}
        </CenteredOverlay>
      )}

      {/* ── Gym Picker Modal ── */}
      {showGymPicker && (
        <ModalOverlay onClick={() => setShowGymPicker(false)}>
          <ModalContainer onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px', borderRadius: '20px', backgroundColor: '#faf6f1', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <ModalHeader style={{ borderBottom: '1px solid #ede8e1', padding: '20px 24px', flexShrink: 0 }}>
              <ModalTitle style={{ fontSize: '20px', fontWeight: 800 }}>
                {pickerMode === 'current' ? 'Change Your Current Gym' : 'Select Your Next Gym'}
              </ModalTitle>
              <CloseButton onClick={() => setShowGymPicker(false)}>
                {/* @ts-ignore */}
                <FaTimes size={18} />
              </CloseButton>
            </ModalHeader>

            {/* Search + Filters */}
            <div style={{ padding: '14px 24px', borderBottom: '1px solid #ede8e1', flexShrink: 0 }}>
              <input
                type="text"
                placeholder="Search gyms…"
                value={gymSearchQuery}
                onChange={e => setGymSearchQuery(e.target.value)}
                style={{ width: '100%', border: '1.5px solid #e5e7eb', borderRadius: '12px', padding: '10px 14px', fontSize: '15px', color: '#111', backgroundColor: '#fff', marginBottom: '14px', boxSizing: 'border-box', outline: 'none' }}
              />
              <p style={{ fontSize: '11px', fontWeight: 700, color: '#9CA3AF', letterSpacing: '1.2px', textTransform: 'uppercase', margin: '0 0 8px' }}>Location</p>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
                {TERRITORIES.map(t => (
                  <FilterChip key={t.id} active={filterTerritory === t.id} onClick={() => setFilterTerritory(filterTerritory === t.id ? null : t.id)}>{t.label}</FilterChip>
                ))}
              </div>
              <p style={{ fontSize: '11px', fontWeight: 700, color: '#9CA3AF', letterSpacing: '1.2px', textTransform: 'uppercase', margin: '0 0 8px' }}>Amenities & Activities</p>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {ALL_AMENITIES.map(a => (
                  <FilterChip key={a} active={filterAmenities.includes(a)} onClick={() => setFilterAmenities(prev => prev.includes(a) ? prev.filter(x => x !== a) : [...prev, a])}>{a}</FilterChip>
                ))}
              </div>
              {(gymSearchQuery || filterTerritory || filterAmenities.length > 0) && (
                <button onClick={() => { setGymSearchQuery(''); setFilterTerritory(null); setFilterAmenities([]); }}
                  style={{ marginTop: '10px', background: 'none', border: 'none', fontSize: '13px', color: '#9CA3AF', cursor: 'pointer', padding: 0 }}>
                  Clear all
                </button>
              )}
            </div>

            <div style={{ padding: '16px 24px', overflowY: 'auto', flex: 1 }}>
              {filteredPickerGyms.length === 0 ? (
                <p style={{ textAlign: 'center', color: '#888', padding: '24px 0' }}>No gyms match your filters.</p>
              ) : (
                <GymGridContainer>
                  {filteredPickerGyms.map((gym: Business) => {
                    const isCurrentGym = gym.name === name;
                    const cooldownInfo = getGymCooldownInfo(gym);
                    const isDisabled = cooldownInfo.inCooldown || isGymSoldOut(gym);
                    const badgeText = isCurrentGym ? 'Your current gym' : isGymSoldOut(gym) ? 'Sold Out' : cooldownInfo.remainingTime;
                    return (
                      <GymGridItem key={gym.businessId} disabled={isDisabled} onClick={() => !isDisabled && handleGymSelected(gym)}>
                        <GymGridItemContent>
                          <GymGridImageContainer>
                            <GymGridImage
                              src={(gym.gallery?.[0] as any)?.imageUrl || (gym.gallery?.[0] as any)?.resourceUrl || `https://via.placeholder.com/60x60?text=${gym.name.charAt(0)}`}
                              alt={gym.name}
                            />
                          </GymGridImageContainer>
                          <GymGridDetails>
                            <GymGridName>{gym.name}</GymGridName>
                            <GymGridAddress>{(typeof gym.address === 'string' ? gym.address : gym.address?.fullAddress) || 'Address unavailable'}</GymGridAddress>
                          </GymGridDetails>
                        </GymGridItemContent>
                        {isDisabled && <UnavailableBadge style={isCurrentGym ? { backgroundColor: '#555' } : {}}>{badgeText}</UnavailableBadge>}
                      </GymGridItem>
                    );
                  })}
                </GymGridContainer>
              )}
            </div>
          </ModalContainer>
        </ModalOverlay>
      )}

      {/* ── Queue Next Gym Confirmation ── */}
      {showQueueConfirm && pendingNextGym && (() => {
        const basicProduct = pendingNextGym.products?.find((p: any) => p.name?.toLowerCase() === 'basic');
        const basicPrice: number = Number(basicProduct?.price?.preTaxCost ?? basicProduct?.preTaxCost ?? 0);
        const premiumProduct = pendingNextGym.products?.find((p: any) => p.name === 'premium' && (p.is_active !== false));
        const premiumPrice: number | null = premiumProduct ? Number(premiumProduct?.price?.preTaxCost ?? premiumProduct?.preTaxCost ?? 0) : null;
        return (
          <CenteredOverlay onClose={() => !queueLoading && setShowQueueConfirm(false)}>
            <MsLabel>QUEUE GYM</MsLabel>
            <MsTitle>Queue {pendingNextGym.name}?</MsTitle>
            <div style={{ fontSize: '15px', color: '#666', lineHeight: 1.6, margin: '12px 0 4px' }}>
              {'Your first month is '}
              <strong style={{ color: '#1a1a1a' }}>${Math.round(basicPrice)}</strong>
              {' plus applicable taxes and fees.'}
              {premiumPrice != null && premiumPrice !== basicPrice && (
                <>
                  {'\n\nThe following month, your membership renews at '}
                  <strong style={{ color: '#1a1a1a' }}>{pendingNextGym.name}</strong>
                  {' at the rate of '}
                  <strong style={{ color: '#1a1a1a' }}>${Math.round(premiumPrice)}/month</strong>
                  {' plus applicable taxes and fees.'}
                </>
              )}
            </div>
            <Divider />
            <MsPrimaryBtn onClick={handleConfirmQueue} disabled={queueLoading}>
              {queueLoading ? 'Processing…' : 'Confirm'}
            </MsPrimaryBtn>
            <MsSecondaryBtn onClick={() => { setShowQueueConfirm(false); setPendingNextGym(null); }} disabled={queueLoading}>
              Go Back
            </MsSecondaryBtn>
          </CenteredOverlay>
        );
      })()}

      {/* ── Switch Current Gym Price Diff Modal ── */}
      {showSwitchConfirm && switchPreviewData && pendingSwitchGym && (() => {
        const { requiresPayment, requiresCredit, difference, newProduct } = switchPreviewData;
        const newPremium = pendingSwitchGym.products?.find((p: any) => p.name === 'premium' && p.is_active !== false);
        const newPremiumPrice: number | null = newPremium
          ? Number(newPremium.price?.preTaxCost ?? newPremium.preTaxCost ?? 0)
          : null;
        const title = requiresPayment ? 'One-Time Adjustment' : requiresCredit ? 'Credit Applied' : 'Switch Gym';
        return (
          <CenteredOverlay onClose={() => !switchLoading && setShowSwitchConfirm(false)}>
            <MsLabel>{title.toUpperCase()}</MsLabel>
            <MsTitle>Switch to {pendingSwitchGym.name}?</MsTitle>
            <div style={{ fontSize: '15px', color: '#666', lineHeight: 1.6, margin: '12px 0' }}>
              {requiresPayment && (
                <>
                  {'A one-time charge of '}
                  <strong style={{ color: '#1a1a1a' }}>${(difference / 100).toFixed(2)}</strong>
                  {' will be applied to cover the price difference for the remainder of this month.'}
                  <br /><br />
                </>
              )}
              {requiresCredit && (
                <>
                  {'A '}
                  <strong style={{ color: '#1a1a1a' }}>${(difference / 100).toFixed(2)} credit</strong>
                  {' will be applied to your account for the price difference this month.'}
                  <br /><br />
                </>
              )}
              {newPremiumPrice != null && (
                <>
                  {'Going forward, your membership at '}
                  <strong style={{ color: '#1a1a1a' }}>{pendingSwitchGym.name}</strong>
                  {' renews at '}
                  <strong style={{ color: '#1a1a1a' }}>${Math.round(newPremiumPrice)} per month</strong>
                  {' plus applicable taxes and fees.'}
                </>
              )}
            </div>
            <Divider />
            <MsPrimaryBtn onClick={handleConfirmSwitch} disabled={switchLoading}>
              {switchLoading ? 'Processing…' : 'Confirm'}
            </MsPrimaryBtn>
            <MsSecondaryBtn onClick={() => { setShowSwitchConfirm(false); setSwitchPreviewData(null); setPendingSwitchGym(null); }} disabled={switchLoading}>
              Go Back
            </MsSecondaryBtn>
          </CenteredOverlay>
        );
      })()}

      {/* ── Stay at Gym Confirmation ── */}
      {showStayModal && (
        <CenteredOverlay onClose={() => !stayConfirmLoading && setShowStayModal(false)}>
          <MsLabel>STAY AT CURRENT GYM</MsLabel>
          <MsTitle>Stay at {name}?</MsTitle>
          <div style={{ fontSize: '15px', color: '#666', lineHeight: 1.6, margin: '12px 0' }}>
            {'This will cancel your upcoming move to '}
            <strong style={{ color: '#1a1a1a' }}>{settings?.nextBusinessName}</strong>
            {'. Your membership will continue at '}
            <strong style={{ color: '#1a1a1a' }}>{name}</strong>
            {currentGymPremiumPrice != null
              ? <> at <strong style={{ color: '#1a1a1a' }}>${Math.round(currentGymPremiumPrice)} per month</strong> plus applicable taxes and fees.</>
              : '.'}
          </div>
          <Divider />
          <MsPrimaryBtn onClick={handleStayAtGym} disabled={stayConfirmLoading}>
            {stayConfirmLoading ? 'Processing…' : 'Confirm'}
          </MsPrimaryBtn>
          <MsSecondaryBtn onClick={() => setShowStayModal(false)} disabled={stayConfirmLoading}>Go Back</MsSecondaryBtn>
        </CenteredOverlay>
      )}

      <FailedPaymentSheet
        open={pastDue}
        onResolved={() => { refreshMembership(); }}
      />

      <ToastContainer />
    </Container>
  );
};

export default DashboardMembers;
